import React, { useEffect, useMemo } from 'react';
import { trackAction } from 'utils/trackAction';
import { makeStyles, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { useGetCurrentUserQuery, useGetMatterTeamQuery } from 'utils/wemble-api';
import { MatterTeamMember } from 'utils/wemble-api.generated';
import organization from 'utils/organization';
import Avatar from '../members/Avatar';
import ActivitySparkline from './ActivitySparkline';
import { useCardTableStyles } from './styles';
import moment from 'moment';

interface MatterModalProps {
  matterNumber: string;
  matterName: string;
  clientName?: string;
  clientCountry?: string;
  confidential?: boolean;
  leadPartnerName?: string;
  leadUserId?: string;
  onUserClick?: (userId: string) => void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    height: '100%',
    minHeight: 400,
    margin: -16,
    [theme.breakpoints.down('sm')]: {
      margin: -15,
    },
    [theme.breakpoints.down(1100)]: {
      flexDirection: 'column',
      height: 'auto',
      minHeight: 'unset',
    },
  },
  sidebar: {
    width: 280,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    [theme.breakpoints.down(1100)]: {
      width: '100%',
      borderRight: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  mainContent: {
    flex: 1,
    padding: theme.spacing(2.5),
    overflowY: 'auto',
    [theme.breakpoints.down(1100)]: {
      padding: theme.spacing(1.5),
      overflowY: 'visible',
    },
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  subtitle: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginTop: 4,
  },
  propertyLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: theme.palette.gray.g2,
    marginBottom: 1,
  },
  propertyCard: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '8px 12px',
  },
  propertyValue: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  hoursCard: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '12px 16px',
    textAlign: 'center' as const,
  },
  hoursValue: {
    fontSize: 22,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
    },
  },
  periodToggle: {
    fontSize: 12,
    padding: '3px 8px',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 600,
    transition: 'color 0.15s',
  },
  periodActive: {
    backgroundColor: '#e8ecf0',
    color: '#333',
  },
  periodInactive: {
    backgroundColor: 'transparent',
    color: '#999',
    '&:hover': {
      color: '#777',
    },
  },
  chartContainer: {
    backgroundColor: theme.palette.gray.g7,
    borderRadius: 8,
    padding: '10px 12px',
  },
  sparkline: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0,
  },
  sparkWeek: {
    display: 'flex',
    flexDirection: 'column' as const,
    borderRight: `1px solid rgba(0,0,0,0.08)`,
    paddingRight: 2,
    marginRight: 2,
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 0,
      marginRight: 0,
    },
  },
  sparkBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1,
    height: 24,
  },
  sparkBarWrap: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minWidth: 2,
    height: '100%',
    padding: '0 1px',
  },
  sparkBar: {
    width: '100%',
    borderRadius: 1,
    backgroundColor: '#6B7FA3',
  },
  sparkWeekLabel: {
    fontSize: 7,
    color: theme.palette.gray.g2,
    opacity: 0.5,
    textAlign: 'left' as const,
    marginTop: 2,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1,
  },
  chartWeeksRow: {
    display: 'flex',
    gap: 0,
  },
  chartWeek: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    minWidth: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: 3,
    marginRight: 3,
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 0,
      marginRight: 0,
    },
  },
  chartBarsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 1,
    height: 60,
  },
  chartBar: {
    flex: 1,
    borderRadius: 2,
    backgroundColor: '#6B7FA3',
    minHeight: 0,
  },
  chartWeekLabel: {
    fontSize: 8,
    color: theme.palette.gray.g2,
    textAlign: 'left' as const,
    textTransform: 'uppercase' as const,
    marginTop: 4,
    whiteSpace: 'nowrap' as const,
  },
}));

/** Aggregate all team members' dailyHours into a single daily total series */
function aggregateDailyHours(members: MatterTeamMember[], days: number): { date: string; hours: number }[] {
  const cutoff = moment().subtract(days, 'days').format('YYYY-MM-DD');
  const dailyMap = new Map<string, number>();

  for (const m of members) {
    for (const d of m.dailyHours || []) {
      if (d.date >= cutoff) {
        dailyMap.set(d.date, (dailyMap.get(d.date) || 0) + d.hours);
      }
    }
  }

  // Fill all weekdays in range
  const result: { date: string; hours: number }[] = [];
  const cursor = moment().subtract(days, 'days');
  const today = moment();
  while (cursor.isSameOrBefore(today, 'day')) {
    if (cursor.isoWeekday() <= 5) {
      const dateStr = cursor.format('YYYY-MM-DD');
      result.push({ date: dateStr, hours: dailyMap.get(dateStr) || 0 });
    }
    cursor.add(1, 'day');
  }

  return result;
}


const DailyHoursChart = ({ members, days }: { members: MatterTeamMember[]; days: number }) => {
  const classes = useStyles();
  const dailyData = useMemo(() => aggregateDailyHours(members, days), [members, days]);
  const maxHours = Math.max(...dailyData.map(d => d.hours), 0.1);

  if (dailyData.length === 0) return null;

  return (
    <div className={classes.chartContainer}>
      <ActivitySparkline
        dailyHours={dailyData}
        maxHours={maxHours}
        showLabels
        height={80}
      />
    </div>
  );
};


const MatterModal = ({ matterNumber, matterName, clientName, clientCountry, confidential, leadPartnerName, leadUserId, onUserClick }: MatterModalProps) => {
  const classes = useStyles();
  const tableClasses = useCardTableStyles();
  const { data: currentUser } = useGetCurrentUserQuery();

  useEffect(() => {
    trackAction('openMatter', { matterNumber, matterName, clientName });
  }, [matterNumber, matterName, clientName]);
  const org = organization(currentUser?.company);
  const enableHours = org.enableDetailedViewHours && (currentUser?.detailedViewHoursPriviliges || ((currentUser?.admin || currentUser?.administratorPriviliges || currentUser?.companyAdmin) && org.detailedViewAdminDefault));

  const { data: rawTeamMembers, isLoading } = useGetMatterTeamQuery({ matterExternalId: matterNumber, days: 30 });

  // Sort lead partner to the top
  const teamMembers = useMemo(() => {
    if (!rawTeamMembers) return undefined;
    return [...rawTeamMembers].sort((a, b) => {
      const aIsLead = leadUserId ? a.userId === leadUserId : a.displayName === leadPartnerName;
      const bIsLead = leadUserId ? b.userId === leadUserId : b.displayName === leadPartnerName;
      if (aIsLead && !bIsLead) return -1;
      if (!aIsLead && bIsLead) return 1;
      return b.totalHours - a.totalHours;
    });
  }, [rawTeamMembers, leadUserId, leadPartnerName]);

  const totalHours = teamMembers?.reduce((sum, m) => sum + m.totalHours, 0) || 0;

  const sparklineData = useMemo(() => aggregateDailyHours(teamMembers || [], 30), [teamMembers]);
  const hasDailyData = sparklineData.filter(d => d.hours > 0).length > 1;
  const sparklineMax = useMemo(() => {
    let max = 0;
    for (const m of teamMembers || []) {
      for (const d of m.dailyHours || []) {
        if (d.hours > max) max = d.hours;
      }
    }
    return max || 1;
  }, [teamMembers]);

  return (
    <div className={classes.container}>
      <div className={classes.sidebar}>
        <div>
          <div className={classes.title}>{matterName}</div>
          {clientName && <div className={classes.subtitle} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {confidential
              ? <LockOutlinedIcon style={{ fontSize: 13, opacity: 0.6 }} />
              : clientCountry ? <span className={`fi fi-${clientCountry.toLowerCase()}`} style={{ fontSize: 13 }} /> : null
            }
            {clientName}
          </div>}
        </div>

        <div>
          <div className={classes.propertyLabel}>Matter Number</div>
          <div className={classes.propertyCard}>
            <div className={classes.propertyValue}>{matterNumber}</div>
          </div>
        </div>

        {leadPartnerName && (
          <div>
            <div className={classes.propertyLabel}>Lead Partner</div>
            <div className={classes.propertyCard}>
              <div className={classes.propertyValue}>{leadPartnerName}</div>
            </div>
          </div>
        )}

        {enableHours && teamMembers && teamMembers.length > 0 && (
          <div>
            <div className={classes.propertyLabel}>Total Hours (last 30 days)</div>
            <div className={classes.hoursCard}>
              <div className={classes.hoursValue}>{totalHours.toFixed(1)}</div>
            </div>
            {hasDailyData && (
              <div style={{ marginTop: 8 }}>
                <DailyHoursChart members={teamMembers} days={30} />
              </div>
            )}
          </div>
        )}

        <div>
          <div className={classes.propertyLabel}>Team Members</div>
          <div className={classes.propertyCard}>
            <div className={classes.propertyValue}>{isLoading ? '...' : teamMembers?.length || 0}</div>
          </div>
        </div>
      </div>

      <div className={classes.mainContent}>
        <h2 style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Team Members (last 30 days)</h2>
        <Table className={tableClasses.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {hasDailyData && <TableCell style={{ width: '40%' }}>Activity</TableCell>}
              {enableHours && <TableCell align={'right'}>Hours</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {teamMembers?.map((member) => (
              <React.Fragment key={member.userId}>
                <TableRow className={onUserClick ? classes.clickableRow : ''} onClick={() => onUserClick?.(member.userId)}>
                  <TableCell>
                    <Avatar size={36} image={member.profilePicture} name={member.displayName} email={member.title} />
                  </TableCell>
                  {hasDailyData && (
                    <TableCell>
                      <ActivitySparkline dailyHours={member.dailyHours || []} maxHours={sparklineMax} showLabels />
                    </TableCell>
                  )}
                  {enableHours && <TableCell align={'right'}>{(member.totalHours || 0).toFixed(1)}</TableCell>}
                </TableRow>
                <TableRow className={tableClasses.spacer} />
              </React.Fragment>
            ))}
            {!isLoading && (!teamMembers || teamMembers.length === 0) && (
              <TableRow>
                <TableCell colSpan={3} style={{ textAlign: 'center', opacity: 0.5 }}>
                  No team members found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default MatterModal;
